import { useUserIdWithLoading } from '@dreamstack/auth'
import {
  useFormatToCurrencyCutoff,
  useFormatToDecimal,
  useTranslation,
} from '@dreamstack/i18n'
import { ArrowRight, ChevronDown, ChevronUp } from '@dreamstack/icons'
import { getOrCreateAnonymousId } from '@dreamstack/tracking/src/getOrCreateAnonymousId'
import { trpc } from '@dreamstack/trpc/client'
import {
  PROPERTY_STATUS_FREE,
  PropertyStatusOpenReservation,
} from '@dreamstack/web-graphql'
import type { PropertyPreview } from '@dreamstack/web/lib/prisma/properties'
import Link from 'next/link'
import { useRouter } from 'next/router'
import type { FunctionComponent } from 'react'
import { useState } from 'react'
import 'twin.macro'
import tw, { styled } from 'twin.macro'
import {
  MoreFlatsSmallText,
  SimpleIconButton,
  SimpleMuiButton,
  blurTextWithMargin,
  useParsePropertyStatus,
  usePropertyPriceVisibility,
} from '../index'
import { sortPropertiesByFloor } from './helper/sortPropertiesByStatus'
import { useGetPropertyUrl } from './routing/useGetPropertyUrl'

const TableColTitle = styled.p<{ align?: 'left' | 'center' | 'right' }>(
  ({ align }) => [
    tw`whitespace-nowrap font-semibold text-accentroGray-full ml:text-xl p-px-12`,
    align === 'left' && tw`text-left`,
    align === 'center' && tw`text-center`,
    align === 'right' && tw`text-right`,
  ]
)

const TableColText = styled.div<{ align?: 'left' | 'center' | 'right' }>(
  ({ align }) => [
    tw`whitespace-nowrap p-px-12 h-full flex flex-row items-center text-base ml:text-xl`,
    align === 'left' && tw`justify-start`,
    align === 'center' && tw`justify-center`,
    align === 'right' && tw`justify-end`,
  ]
)

const TableRow = styled.div<{ idx: number }>(({ idx }) => [
  tw`contents`,
  // TODO: Activate when Tailwind Version > v3.1
  // idx % 2 && tw`[&>*]:bg-accentroAqua-100`,
  idx % 2 && tw`all-child:bg-accentroAqua-100 bg-black`,
])

const StyledStatus = styled.div<{ status: number }>(({ status }) => [
  tw`px-px-6 font-semibold`,
  PropertyStatusOpenReservation.includes(status)
    ? tw`bg-accentroYellowLight-full text-accentroSecondaryBrown-full`
    : PROPERTY_STATUS_FREE.includes(status)
    ? tw`bg-accentroSuccessLight-full text-accentroSuccess-full`
    : tw`bg-accentroError-100 text-accentroError-full`,
])

const StyledStatusCircle = styled.div<{ status: number }>(({ status }) => [
  tw`w-px-16 h-px-16 rounded-full`,
  PropertyStatusOpenReservation.includes(status)
    ? tw`bg-accentroYellowLight-full`
    : PROPERTY_STATUS_FREE.includes(status)
    ? tw`bg-accentroSuccess-full`
    : tw`bg-accentroError-full`,
])

const StyledPropertyTable = styled.div<{ showAllProperties: boolean }>(
  ({ showAllProperties }) => [
    tw`overflow-y-hidden`,
    showAllProperties ? tw`max-height[none]` : tw`max-height[300px]`,
  ]
)

export const MoreFlatsList: FunctionComponent<
  React.PropsWithChildren<{
    properties: PropertyPreview[]
  }>
> = ({ properties }) => {
  const [showAllProperties, setShowAllProperties] = useState(false)
  // Sorting priorities by Floor
  properties = sortPropertiesByFloor(properties)
  const formatCurrency = useFormatToCurrencyCutoff()
  const formatDecimal = useFormatToDecimal()
  const t = useTranslation()
  const { userId } = useUserIdWithLoading()
  const getPropertyUrl = useGetPropertyUrl()
  const parsePropertyStatus = useParsePropertyStatus()
  const propertyPriceVisibility = usePropertyPriceVisibility()
  const router = useRouter()
  const { mutateAsync: trackButtonClickServer } =
    trpc.anonymousTracking.trackEvent.useMutation()

  const trackClickCallToActionButton = async ({
    propertyId,
  }: {
    propertyId: number
  }) => {
    const anonymousUserId = getOrCreateAnonymousId()
    await trackButtonClickServer({
      eventName: 'buttonClick',
      userId,
      anonymousUserId,
      data: {
        buttonAction: 'Weitere Wohnungen',
        url: router.asPath,
        additionalInfo: {
          propertyId,
        },
      },
    })
  }

  return (
    <div tw="mt-px-40 col-span-full ml:(mt-px-80) w-full flex flex-col gap-6">
      <div tw="box-shadow[rgb(0 0 0 / 10%) -10px 0px 10px -5px inset] sm:box-shadow[none]">
        <StyledPropertyTable showAllProperties={showAllProperties}>
          <div tw="w-full grid grid-cols-[repeat(3, min-content) auto repeat(2, min-content)] md:grid-cols-[repeat(4, min-content) auto repeat(2, min-content)] items-end">
            <div tw="contents all-child:(border-b-2 border-accentroGray-200)">
              <TableColTitle align="left" tw="hidden md:block">
                ID
              </TableColTitle>
              <TableColTitle align="center">
                {t('accentro:moreFlatsList.floor')}
              </TableColTitle>
              <TableColTitle align="center">
                {t('accentro:moreFlatsList.rooms')}
              </TableColTitle>
              <TableColTitle align="center" tw="">
                {t('accentro:moreFlatsList.livingSpace')}
              </TableColTitle>
              <TableColTitle align="right">
                {t('accentro:moreFlatsList.price')}
              </TableColTitle>
              <TableColTitle align="center">Status</TableColTitle>
              <div tw="h-full" />
            </div>
            {properties.map((property, idx) => {
              const formattedStatus = parsePropertyStatus(property.status)
              const formattedPrice = propertyPriceVisibility(property)
                ? formatCurrency(property.price!)
                : '???'
              const formattedSqm = property.property_space_value
                ? formatDecimal(property.property_space_value)
                : ''

              const floorNum = property.floor
                ? parseInt(property.floor) === 0
                  ? t('accentro:property.groundFloorAbbreviation')
                  : property.floor + '.'
                : null

              const count = property.number_of_rooms ?? 0

              return (
                <TableRow style={{}} idx={idx} key={property.name}>
                  <TableColText align="left" tw="hidden md:flex">
                    {property.name}
                  </TableColText>
                  <TableColText align="center">{floorNum ?? '-'}</TableColText>
                  <TableColText align="center">{count}</TableColText>
                  <TableColText align="center">
                    <span>
                      {formattedSqm} {t('accentro:squareMeter')}
                    </span>
                  </TableColText>
                  <TableColText align="right">
                    <span
                      css={
                        !propertyPriceVisibility(property)
                          ? blurTextWithMargin
                          : undefined
                      }
                      tw="relative font-semibold text-accentroGray-full"
                    >
                      {formattedPrice}{' '}
                      <MoreFlatsSmallText>EUR</MoreFlatsSmallText>
                    </span>
                  </TableColText>
                  <TableColText align="center">
                    <div tw="hidden md:block">
                      <StyledStatus status={property.status as number}>
                        {formattedStatus}
                      </StyledStatus>
                    </div>
                    <div tw="md:hidden">
                      <StyledStatusCircle status={property.status as number} />
                    </div>
                  </TableColText>
                  <TableColText>
                    <Link
                      href={getPropertyUrl({
                        id: property.id,
                        city: property.city ?? undefined,
                      })}
                    >
                      <SimpleIconButton
                        onClick={() =>
                          trackClickCallToActionButton({
                            propertyId: property.id,
                          })
                        }
                      >
                        <ArrowRight tw="text-black" />
                      </SimpleIconButton>
                    </Link>
                  </TableColText>
                </TableRow>
              )
            })}
          </div>
        </StyledPropertyTable>
      </div>
      {properties.length > 3 && (
        <div tw="flex justify-center items-center">
          <SimpleMuiButton
            endIcon={showAllProperties ? <ChevronUp /> : <ChevronDown />}
            onClick={() => setShowAllProperties(!showAllProperties)}
          >
            {showAllProperties
              ? t('accentro:moreFlatsList.lessApartments')
              : t('accentro:moreFlatsList.allApartments')}
          </SimpleMuiButton>
        </div>
      )}
    </div>
  )
}
